import { queryOptions, skipToken, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { goalKeys, parseGoal, type Goal } from './entity';

const STALE_TIME = 60_000;

/* ------------------------------------------------------------------------- */
/* getGoals — full list (the endpoint is not paginated)                        */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getGoals(workspaceId: number, qc: QueryClient): Promise<readonly Goal[]> {
  const raw = await hydraUiApi.goals.list({
    params: { path: { environment_id: String(workspaceId) } },
  });
  const parsed = (raw.goals ?? []).map(parseGoal);
  for (const g of parsed) qc.setQueryData(goalKeys.detail(workspaceId, g.id), g);
  return parsed;
}

/** @public */
export const getGoalsQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: goalKeys.list(workspaceId, undefined),
    queryFn: () => getGoals(workspaceId, qc),
    staleTime: STALE_TIME,
  });

/* ------------------------------------------------------------------------- */
/* getGoal — by id                                                            */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getGoal(workspaceId: number, goalId: string, qc: QueryClient): Promise<Goal> {
  const raw = await hydraUiApi.goals.get({
    params: { path: { environment_id: String(workspaceId), id: goalId } },
  });
  if (raw.goal == null) {
    throw new Error(`goal ${goalId} not found`);
  }
  const parsed = parseGoal(raw.goal);
  qc.setQueryData(goalKeys.detail(workspaceId, parsed.id), parsed);
  return parsed;
}

/** @public */
export const getGoalQueryOptions = (workspaceId: number, goalId: string, qc: QueryClient) =>
  queryOptions({
    queryKey: goalKeys.detail(workspaceId, goalId),
    queryFn: () => getGoal(workspaceId, goalId, qc),
    staleTime: STALE_TIME,
  });

interface UseGoalOptionalOptions {
  workspaceId: number;
  id: string;
  enabled?: boolean;
}

/**
 * Non-suspense, conditionally-enabled by-id hook (see `useSnippetOptional`).
 *
 * @public
 */
export const useGoalOptional = ({ workspaceId, id, enabled = true }: UseGoalOptionalOptions) => {
  const qc = useQueryClient();
  const active = enabled && workspaceId > 0 && id !== '';
  return useQuery({
    queryKey: active ? goalKeys.detail(workspaceId, id) : ['goal', 'inactive'],
    queryFn: active ? () => getGoal(workspaceId, id, qc) : skipToken,
    staleTime: STALE_TIME,
    retry: false,
  });
};
